import { Box, Typography, Modal, ModalDialog, ModalClose } from "@mui/joy";
import { ReactNode } from "react";

import Button from "../Button";

interface DialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    children: ReactNode;
    /** Max dialog width in px (or CSS size). Defaults to 720 to match map picker. */
    maxWidth?: number | string;
    showCloseButton?: boolean;
    /** When true, children are not padded (e.g. full-bleed map). */
    disableContentPadding?: boolean;
    actions?: {
        cancel?: {
            label?: string;
            onClick?: () => void;
            disabled?: boolean;
        };
        submit?: {
            label: string;
            onClick: () => void;
            disabled?: boolean;
            loading?: boolean;
            variant?: "soft" | "solid" | "outlined" | "plain";
            color?: "primary" | "neutral" | "danger" | "success" | "warning";
        };
    };
}

/**
 * App modal chrome — matches quick-project location picker:
 * 4px radius, flush padding, title-md header, soft primary / outlined cancel actions.
 */
export default function Dialog({
    open,
    onClose,
    title,
    description,
    children,
    maxWidth = 720,
    showCloseButton = true,
    disableContentPadding = false,
    actions,
}: DialogProps) {
    const handleCancel = () => {
        if (actions?.cancel?.onClick) {
            actions.cancel.onClick();
        } else {
            onClose();
        }
    };

    const maxWidthCss =
        typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog
                variant="outlined"
                sx={{
                    width: `min(${maxWidthCss}, 94vw)`,
                    maxWidth: maxWidthCss,
                    p: 0,
                    overflow: "hidden",
                    borderRadius: "4px",
                    maxHeight: "90vh",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        px: 2,
                        pt: 1.75,
                        pb: 1,
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ minWidth: 0, pr: 2 }}>
                        <Typography level="title-md" sx={{ fontWeight: 700 }}>
                            {title}
                        </Typography>
                        {description ? (
                            <Typography
                                level="body-sm"
                                sx={{ color: "text.secondary" }}
                            >
                                {description}
                            </Typography>
                        ) : null}
                    </Box>
                    {showCloseButton ? <ModalClose /> : null}
                </Box>

                <Box
                    sx={{
                        px: disableContentPadding ? 0 : 2,
                        py: disableContentPadding ? 0 : 1.5,
                        overflow: "auto",
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    {children}
                </Box>

                {actions ? (
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                            flexWrap: "wrap",
                            px: 2,
                            pt: disableContentPadding ? 1.5 : 0,
                            pb: 1.5,
                            flexShrink: 0,
                        }}
                    >
                        {actions.cancel ? (
                            <Button
                                type="button"
                                variant="outlined"
                                color="neutral"
                                onClick={handleCancel}
                                disabled={actions.cancel.disabled}
                            >
                                {actions.cancel.label || "Cancel"}
                            </Button>
                        ) : null}
                        {actions.submit ? (
                            <Button
                                type="button"
                                variant={actions.submit.variant || "soft"}
                                color={actions.submit.color || "primary"}
                                onClick={actions.submit.onClick}
                                disabled={actions.submit.disabled}
                                loading={actions.submit.loading}
                            >
                                {actions.submit.label}
                            </Button>
                        ) : null}
                    </Box>
                ) : null}
            </ModalDialog>
        </Modal>
    );
}
